import { FC, useEffect, ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { paymentSelectors, userSelectors } from '@/redux/selectors';
import { useAppSelector } from '@/redux/hooks';
import { AuthProviderEnum, PageDescription, SubmenuType } from '@/config';
import { ProfileInfo, LangSwitcher, SvgIcon, BalanceInfo } from '@/components';
import useDevice from '@/hooks/useDevice';

import amlbotLogo from '@/assets/images/logo-amlbot.svg';

import { HelpMenu } from './components/HelpMenu';
import { findCurrentPage } from './Header.helpers';
import styles from './Header.module.sass';

interface HeaderProps {
  isActiveTariffsPanel: boolean;
  children?: ReactNode;
  onToggleTariffsPanel: (isActive: boolean) => void;
}

const Header: FC<HeaderProps> = ({
  children,
  isActiveTariffsPanel,
  onToggleTariffsPanel,
}) => {
  const { data } = useAppSelector(userSelectors.getUserSlice);
  const balance = useAppSelector(userSelectors.getUserBalance);
  const isUserLocked = useAppSelector(userSelectors.checkIfUserLocked);
  const plans = useAppSelector(paymentSelectors.getPaymentPlans);

  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState<PageDescription | SubmenuType | null>(null);

  const hasBackPath = page?.backPath;
  const isPromo = data?.promoBalance;

  useEffect(() => {
    const foundPage = findCurrentPage(location.pathname);
    setPage(foundPage);
  }, [location, t]);

  const backHandler = () => {
    if (page && page.backPath) {
      navigate(page.backPath, {
        state: {
          ...(!!location?.state?.queryHash
            ? { queryHash: location?.state?.queryHash }
            : {}),
          filter: {
            ...(!!location?.state?.filter ? location?.state?.filter : {}),
          },
        },
      });
    }
  };

  const prepareUserName = () => {
    if (
      data?.provider === AuthProviderEnum.GOOGLE ||
      data?.provider === AuthProviderEnum.DEFAULT
    ) {
      return data?.fullName || data.emailAddress;
    }
    return data?.fullName || 'user';
  };

  const classes = cx(styles.header, {
    [styles['has-back']]: hasBackPath,
  });

  return (
    <header className={classes}>
      {isMobile && (
        <Link to="/" className={styles.logo}>
          <img src={amlbotLogo} alt="AMLBot" />
        </Link>
      )}

      {page && hasBackPath && (
        <button
          type="button"
          className={cx(styles.headline, styles['is-clickable'])}
          onClick={backHandler}
        >
          <span className={styles.back}>
            <SvgIcon name="arrow-back" />
          </span>
          <span className={styles.title}>{t(page.i18NameKey || '')}</span>
        </button>
      )}

      {page && !hasBackPath && (
        <div className={styles.headline}>
          <span className={styles.title}>{t(page.i18NameKey || '')}</span>
        </div>
      )}
      <div className={styles.actions}>
        <BalanceInfo
          isActive={isActiveTariffsPanel}
          balance={balance}
          plans={plans}
          isUserLocked={isUserLocked}
          isPromo={isPromo}
          onToggle={onToggleTariffsPanel}
        />

        <LangSwitcher isShowArrow={false} isRounded />

        <HelpMenu />

        <ProfileInfo name={prepareUserName()} />
      </div>

      {children}
    </header>
  );
};

export default Header;
